// PagedTable.js  
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Table, Pagination } from 'antd';
import './index.scss';
import background from 'asset/img/bk-person.webp';
import title_rank from 'asset/img/title-rank.png';
import back from 'asset/img/back.png';
import { useNavigate, useLocation } from 'react-router-dom';
import avator from 'asset/img/avator.webp';
import { getRrankingList } from 'network';
import { formatTimeHM } from 'utils';
import { RANK_FAKE_LIST_WITH_IDS } from '../../const/rankFakeList';

// 模块级排序函数，避免每次 effect 新建函数，减少 GC 压力
function compareRankItem(a, b) {
  const sa = Number(a.total_score);
  const sb = Number(b.total_score);
  if (sb !== sa) return sb - sa;
  const da = Number(a.total_done_count);
  const db = Number(b.total_done_count);
  if (db !== da) return db - da;
  return Number(a.total_sec) - Number(b.total_sec);
}

// 静态数据移到组件外部
const RANK_PODIUM = [{ value: 2 }, { value: 1 }, { value: 3 }];
const RANK_TAB = [
    { name: '全国总排名', key: 0 },
    { name: '班级总排名', key: 2 },
];

// 截断字符串的工具函数
const truncateString = (str) => {
    if (!str) return '';
    if (str.length > 4) {
        return str.slice(0, 4) + '...';
    }
    return str;
};

const Rank = () => {
    const [data, setData] = useState([]);
    const [rankQueue, setRankQueue] = useState([]);
    const [loading, setLoading] = useState(false);
    const [rank, setRank] = useState(1);
    const [rankIndex, setRankIndex] = useState(0);
    const [info, setInfo] = useState({});
    let navigate = useNavigate();
    // 页面类型
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const type = searchParams.get('type');

    useEffect(() => {
        let isMounted = true;
        
        // 现在全是总排名
        const sort = 'total_score';
        // 全国取真实前 100 条，班级取 30 条；全国再与 70 条写死假数据合并排序后取前 100 展示
        const pageSize = rankIndex === 0 ? 100 : 30;
        getRrankingList({ sort: sort, size: pageSize, invite: rankIndex === 1 ? '1' : '' }).then(res => {
            if (!isMounted) return;
            const rankData = res?.data?.rank_list?.filter(item => item.user_id != null && item.user_id !== '') || [];
            let list = rankData;
            let queue = [rankData[1], rankData[0], rankData[2]].filter(Boolean);

            let myRank = res?.data?.my_order_num;

            // 全国总排名：真实前 100 + 70 条预计算假数据，合并后排序取前 100；「我的」排名按合并结果计算
            if (rankIndex === 0) {
                list = rankData.concat(RANK_FAKE_LIST_WITH_IDS).sort(compareRankItem).slice(0, 100);
                queue = [list[1], list[0], list[2]].filter(Boolean);
                const myUserId = res?.data?.my_info?.user_id;
                if (myUserId == null) {
                    myRank = '100+';
                } else {
                    const myId = String(myUserId);
                    let myIndex = -1;
                    for (let i = 0; i < list.length; i++) {
                        if (String(list[i].user_id) === myId) { myIndex = i; break; }
                    }
                    myRank = myIndex >= 0 ? myIndex + 1 : '100+';
                }
            }

            setData(list);
            setInfo(res?.data?.my_info);
            setRank(myRank);
            setRankQueue(queue);
        });
        
        return () => {
            isMounted = false;
        };
    }, [rankIndex]);

    // 使用 useMemo 缓存表格列配置
    const columns = useMemo(() => [
        {
            title: '排名',
            render: (_, record, index) => index + 1,
            key: 'rank',
        },
        {
            title: '学生',
            dataIndex: 'name',
            key: 'name',
            render: (text, record) => (
                <div className='student'>
                    <div className='img'>
                        <img
                            src={record?.avatar || avator}
                            onError={(e) => {
                                e.target.src = avator;
                            }} 
                            alt=""
                        />
                    </div>
                    <span>{truncateString(record?.name)}</span>
                </div>
            ),
        },
        {
            title: '闯关数',
            dataIndex: 'total_done_count',
            key: 'total_done_count',
            render: (text, record) => record?.total_done_count,
        },
        {
            title: '成绩',
            dataIndex: 'total_score',
            key: 'total_score',
            render: (text, record) => record?.total_score,
        },
        {
            title: '总时长',
            dataIndex: 'total_sec',
            key: 'total_sec',
            render: (text) => <span>{formatTimeHM(text)}</span>,
        },
    ], []);

    const handleBack = useCallback(() => {
        navigate(`/home?type=${type}`);
    }, [navigate, type]);

    const changeRank = useCallback((index) => {
        setRankIndex(index);
    }, []);
    return (
        <div className='rank-container'>
            <div className='rank-back' onClick={handleBack}><img src={back} /></div>
            <div className='rank-title'><img src={title_rank} /></div>
            <div className='rank-header'>
                {RANK_TAB.map((item, index) => (
                    <div 
                        className={`header-item ${rankIndex === index ? 'active-class' : ''}`} 
                        key={item.key} 
                        onClick={() => changeRank(index)}
                    >
                        {item.name}
                    </div>
                ))}
            </div>
            <div className='rank-queue'>
                {rankQueue.map((item, index) => {
                    return <div className='item' key={index}>
                        <div className='avator'><img
                            src={item?.avatar || avator}
                            onError={(e) => {
                                e.target.src = avator;
                            }}
                            alt="头像"
                        /></div>
                        <div className='name'>{item?.name}</div>
                    </div>
                })}
            </div>
            <div className='rank-podium'>
                {RANK_PODIUM.map((item, index) => (
                    <div className='item' key={index}>
                        <div className='name'>{item.value}</div>
                    </div>
                ))}
            </div>
            <div className='rank-table'>
                <Table
                    columns={columns}
                    dataSource={data}
                    loading={loading}
                    className='rank-table'
                    rowClassName="custom-table-row"
                    pagination={false} // 我们将使用自定义的分页组件  
                    rowKey="user_id"
                />
            </div>
            <div className='rank-me'>
                <div className='rank'>{rank}</div>
                <div className='avator'><img src={info?.avatar || avator} alt="头像" onError={(e) => { e.target.src = avator; }} /></div>
                <div className='name'>{info?.name}</div>
                <div className='number'>{info?.total_done_count}</div>
                <div className='score'>{info?.total_score}</div>
                <div className='time'>{formatTimeHM(info?.total_sec)}</div>
            </div>
            <img className='rank-background' src={background} />
        </div >
    );
};

export default Rank;